import { type VariantProps } from 'class-variance-authority';
import { HTMLAttributes, ReactNode } from 'react';
import {
  alertVariants,
  ALERT_VARIANT_ICONS,
} from '@/shared/constants/ui-variants';
import { cn } from '@/shared/lib/utils';
import { AlertVariant } from '@/shared/types/alert.types';

export interface AlertProps
  extends HTMLAttributes<HTMLDivElement>, VariantProps<typeof alertVariants> {
  readonly icon?: ReactNode;
  readonly showIcon?: boolean;
}

export function Alert({
  className,
  variant = AlertVariant.Default,
  align = 'start',
  valign = 'start',
  icon,
  showIcon = true,
  children,
  ...props
}: AlertProps) {
  const Icon = variant
    ? ALERT_VARIANT_ICONS[variant]
    : ALERT_VARIANT_ICONS[AlertVariant.Default];

  return (
    <div
      role="alert"
      className={cn(alertVariants({ variant, align, valign }), className)}
      {...props}
    >
      {showIcon && (icon ?? <Icon aria-hidden="true" />)}
      <div className="flex-1">{children}</div>
    </div>
  );
}

export type AlertTitleProps = Readonly<HTMLAttributes<HTMLHeadingElement>>;

export function AlertTitle({ className, children, ...props }: AlertTitleProps) {
  return (
    <h5
      className={cn('mb-1 font-medium leading-none tracking-tight', className)}
      {...props}
    >
      {children}
    </h5>
  );
}

export type AlertDescriptionProps = Readonly<
  HTMLAttributes<HTMLParagraphElement>
>;

export function AlertDescription({
  className,
  ...props
}: AlertDescriptionProps) {
  return (
    <div
      className={cn('text-sm [&_p]:leading-relaxed', className)}
      {...props}
    />
  );
}
